import type { ChecklistTemplateItem } from '@qlk/shared';
import { ListChecks } from 'lucide-react';
import { useId, useState } from 'react';

import { EmptyState, Nut } from '../../../ui/index.js';
import { useChecklist, useLuuChecklist } from '../api.js';

const O_NHAP =
  'vung-bam w-full rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu placeholder:text-chu-mo';

function khoaTuNhan(nhan: string): string {
  return (
    nhan
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/đ/gi, 'd')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'muc_moi'
  );
}

/** Một dòng checklist: sửa nhãn tại chỗ và bật/tắt. */
function DongChecklist({ muc, coSua }: { muc: ChecklistTemplateItem; coSua: boolean }) {
  const luu = useLuuChecklist();
  const [nhan, setNhan] = useState(muc.label);
  const id = useId();

  const daDoi = nhan.trim() !== muc.label;

  return (
    <li className="flex flex-wrap items-center gap-3 px-6 py-4">
      <input
        id={id}
        type="checkbox"
        checked={muc.active}
        disabled={!coSua || luu.isPending}
        aria-label={`Bật mục "${muc.label}"`}
        onChange={(e) => {
          luu.mutate({
            key: muc.key,
            input: { key: muc.key, label: muc.label, order: muc.order, active: e.target.checked },
          });
        }}
        className="size-5 shrink-0 accent-nhan"
      />

      {coSua ? (
        <input
          value={nhan}
          aria-label={`Nội dung mục ${String(muc.order)}`}
          onChange={(e) => {
            setNhan(e.target.value);
          }}
          className={`${O_NHAP} flex-1 ${muc.active ? '' : 'text-chu-mo line-through'}`}
        />
      ) : (
        <span
          className={`flex-1 text-co-ban ${muc.active ? 'text-chu' : 'text-chu-mo line-through'}`}
        >
          {muc.label}
        </span>
      )}

      {coSua && daDoi && (
        <Nut
          kieu="chinh"
          dangChay={luu.isPending}
          onClick={() => {
            luu.mutate({
              key: muc.key,
              input: {
                key: muc.key,
                label: nhan.trim(),
                order: muc.order,
                active: muc.active,
              },
            });
          }}
        >
          Lưu
        </Nut>
      )}
    </li>
  );
}

/** Khung "Checklist trước khi đăng": sửa nội dung template và thêm mục mới. */
export function KhungChecklist({ coSua }: { coSua: boolean }) {
  const checklist = useChecklist();
  const luu = useLuuChecklist();
  const [mucMoi, setMucMoi] = useState('');
  const id = useId();

  const danhSach = checklist.data ?? [];

  return (
    <section
      aria-labelledby="checklist-tieu-de"
      className="rounded-the border border-vien bg-be-mat"
    >
      <div className="border-b border-vien px-6 py-4">
        <h2 id="checklist-tieu-de" className="text-tieu-de-khoi font-semibold text-chu">
          Checklist trước khi đăng
        </h2>
        <p className="mt-1 text-co-ban text-chu-phu">
          Module Sản xuất nội dung đọc mẫu này cho mỗi video.
        </p>
      </div>

      {checklist.isPending && (
        <p className="px-6 py-5 text-co-ban text-chu-phu">Đang tải checklist…</p>
      )}

      {checklist.error && (
        <p role="alert" className="px-6 py-5 text-co-ban text-bad-chu">
          {checklist.error.message}
        </p>
      )}

      {!checklist.isPending && !checklist.error && danhSach.length === 0 && (
        <EmptyState
          icon={ListChecks}
          title="Chưa có mục nào"
          description="Thêm mục đầu tiên để nhân sự có thứ tự kiểm tra trước khi đăng."
        />
      )}

      <ul className="divide-y divide-vien">
        {danhSach.map((m) => (
          <DongChecklist key={m.key} muc={m} coSua={coSua} />
        ))}
      </ul>

      {coSua && (
        <form
          className="flex flex-wrap items-center gap-3 border-t border-vien px-6 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            const nhan = mucMoi.trim();
            if (!nhan) return;
            luu.mutate(
              {
                key: khoaTuNhan(nhan),
                input: {
                  key: khoaTuNhan(nhan),
                  label: nhan,
                  order: (danhSach.at(-1)?.order ?? 0) + 1,
                  active: true,
                },
              },
              {
                onSuccess: () => {
                  setMucMoi('');
                },
              },
            );
          }}
        >
          <label htmlFor={`${id}-moi`} className="sr-only">
            Mục checklist mới
          </label>
          <input
            id={`${id}-moi`}
            value={mucMoi}
            onChange={(e) => {
              setMucMoi(e.target.value);
            }}
            placeholder="Thêm mục kiểm tra mới…"
            className={`${O_NHAP} flex-1`}
          />
          <Nut kieu="phu" type="submit" dangChay={luu.isPending}>
            Thêm mục
          </Nut>
        </form>
      )}

      {luu.error && (
        <p role="alert" className="px-6 pb-4 text-co-ban text-bad-chu">
          {luu.error.message}
        </p>
      )}
    </section>
  );
}
